import React, { useState, useEffect, useCallback, useMemo } from 'react'
import { WizardState, PoolBreakdown } from '../Index'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Filter,
  Loader2, Check, RefreshCw, BarChart2, Globe,
  FolderTree, ChevronDown, ChevronRight, Minus, Search, Layers, BookOpen
} from 'lucide-react'

interface Props {
  state: WizardState
  updateState: (patch: Partial<WizardState>) => void
  standards: { id: number; name: string }[]
  tags: string[]
}

interface ChapterItem {
  id: number
  name: string
  subject_id: number
  count: number
  standards: { id: number; name: string }[]
  sections: { id: number; name: string; questions_count: number }[]
}

interface SubjectItem {
  id: number
  name: string
  count: number
  chapters: ChapterItem[]
}

const DIFFICULTY_OPTIONS = ['Easy', 'Medium', 'Hard']
const QUESTION_TYPE_OPTIONS = ['mcq', 'multi_correct', 'numerical', 'true_false', 'assertion_reason', 'match_following', 'subjective']

export default function StepPoolFilters({ state, updateState, standards, tags }: Props) {
  const [loadingPool, setLoadingPool] = useState(false)
  const [taxonomySearch, setTaxonomySearch] = useState('')
  const [expandedSubjects, setExpandedSubjects] = useState<Record<number, boolean>>({})
  const [viewMode, setViewMode] = useState<'by_section' | 'all_subjects'>('by_section')

  const pool = state.poolBreakdown

  const selectedSections = useMemo(() => {
    return (state.blueprintSections || []).filter((bs: any) => bs._selected !== false)
  }, [state.blueprintSections])

  // Extract all available subjects & chapters with baseline counts from selected syllabi
  const selectedSyllabi = useMemo(() => {
    return state.syllabiData.filter(s => state.selectedSyllabiIds.includes(s.id))
  }, [state.syllabiData, state.selectedSyllabiIds])

  // Build unified taxonomy hierarchy tree: Subject -> Chapters
  const taxonomyTree: SubjectItem[] = useMemo(() => {
    const subjectsMap = new Map<number, {
      id: number
      name: string
      count: number
      chaptersMap: Map<number, ChapterItem>
    }>()

    selectedSyllabi.forEach(syl => {
      syl.subjects.forEach(sub => {
        const subId = sub.subject_id || sub.id
        if (!subId) return
        if (!subjectsMap.has(subId)) {
          subjectsMap.set(subId, {
            id: subId,
            name: sub.name,
            count: 0,
            chaptersMap: new Map()
          })
        }
        const sEntry = subjectsMap.get(subId)!
        sEntry.count += (sub.questions_count || 0)

        sub.chapters.forEach(chap => {
          const chapId = chap.chapter_id || chap.id
          if (!chapId) return
          if (!sEntry.chaptersMap.has(chapId)) {
            sEntry.chaptersMap.set(chapId, {
              id: chapId,
              name: chap.name,
              subject_id: subId,
              count: 0,
              standards: chap.standards || [],
              sections: (chap.sections || []).map(sec => ({
                id: sec.id,
                name: sec.name,
                questions_count: sec.questions_count || 0
              }))
            })
          }
          const cEntry = sEntry.chaptersMap.get(chapId)!
          cEntry.count += (chap.questions_count || 0)
        })
      })
    })

    return Array.from(subjectsMap.values()).map(sub => ({
      id: sub.id,
      name: sub.name,
      count: sub.count,
      chapters: Array.from(sub.chaptersMap.values())
    }))
  }, [selectedSyllabi])

  // Auto-expand all subjects by default when taxonomyTree loads
  useEffect(() => {
    if (taxonomyTree.length > 0) {
      setExpandedSubjects(prev => {
        const next = { ...prev }
        let hasNew = false
        taxonomyTree.forEach(sub => {
          if (next[sub.id] === undefined) {
            next[sub.id] = true
            hasNew = true
          }
        })
        return hasNew ? next : prev
      })
    }
  }, [taxonomyTree])

  const allSubjectIds = useMemo(() => taxonomyTree.map(s => s.id), [taxonomyTree])
  const allChapterIds = useMemo(() => taxonomyTree.flatMap(s => s.chapters.map(c => c.id)), [taxonomyTree])

  const isAllTaxonomyIncluded = state.filterSubjectIds.length === 0 && state.filterChapterIds.length === 0
  const isNoneTaxonomyIncluded = state.filterSubjectIds.includes(-1) || state.filterChapterIds.includes(-1)

  // Compute active selected chapter set
  const selectedChapterIdsSet = useMemo(() => {
    if (isNoneTaxonomyIncluded) return new Set<number>()
    if (isAllTaxonomyIncluded) return new Set<number>(allChapterIds)
    if (state.filterChapterIds.length > 0) return new Set<number>(state.filterChapterIds.filter(id => id > 0))
    if (state.filterSubjectIds.length > 0) {
      const chaps = taxonomyTree
        .filter(s => state.filterSubjectIds.includes(s.id))
        .flatMap(s => s.chapters.map(c => c.id))
      return new Set<number>(chaps)
    }
    return new Set<number>()
  }, [isAllTaxonomyIncluded, isNoneTaxonomyIncluded, allChapterIds, state.filterChapterIds, state.filterSubjectIds, taxonomyTree])

  // Compute active selected subject set
  const selectedSubjectIdsSet = useMemo(() => {
    if (isNoneTaxonomyIncluded) return new Set<number>()
    if (isAllTaxonomyIncluded) return new Set<number>(allSubjectIds)
    if (state.filterChapterIds.length > 0) {
      const activeSubs = new Set<number>()
      taxonomyTree.forEach(sub => {
        if (sub.chapters.length === 0) {
          if (state.filterSubjectIds.includes(sub.id)) activeSubs.add(sub.id)
        } else {
          const hasSelectedChap = sub.chapters.some(ch => selectedChapterIdsSet.has(ch.id))
          if (hasSelectedChap) activeSubs.add(sub.id)
        }
      })
      return activeSubs
    }
    if (state.filterSubjectIds.length > 0) {
      return new Set<number>(state.filterSubjectIds.filter(id => id > 0))
    }
    return new Set<number>()
  }, [isAllTaxonomyIncluded, isNoneTaxonomyIncluded, allSubjectIds, state.filterChapterIds, state.filterSubjectIds, taxonomyTree, selectedChapterIdsSet])

  // Determine tri-state for a subject: 'checked' | 'indeterminate' | 'unchecked'
  const getSubjectCheckState = (sub: SubjectItem): 'checked' | 'indeterminate' | 'unchecked' => {
    if (sub.chapters.length === 0) {
      return selectedSubjectIdsSet.has(sub.id) ? 'checked' : 'unchecked'
    }
    const selectedCount = sub.chapters.filter(ch => selectedChapterIdsSet.has(ch.id)).length
    if (selectedCount === sub.chapters.length) return 'checked'
    if (selectedCount > 0) return 'indeterminate'
    return 'unchecked'
  }

  // Handle Subject Checkbox toggle
  const handleToggleSubject = (subjectId: number) => {
    const sub = taxonomyTree.find(s => s.id === subjectId)
    if (!sub) return

    const currentState = getSubjectCheckState(sub)
    const newChaptersSet = new Set(selectedChapterIdsSet)
    const newSubjectsSet = new Set(selectedSubjectIdsSet)

    if (currentState === 'checked' || currentState === 'indeterminate') {
      // Deselect subject and its chapters
      sub.chapters.forEach(ch => newChaptersSet.delete(ch.id))
      newSubjectsSet.delete(subjectId)
    } else {
      // Select subject and all its chapters
      sub.chapters.forEach(ch => newChaptersSet.add(ch.id))
      newSubjectsSet.add(subjectId)
    }

    const newChapterIds = Array.from(newChaptersSet)
    const newSubjectIds = Array.from(newSubjectsSet)

    if (newChapterIds.length === 0 && newSubjectIds.length === 0) {
      updateState({ filterSubjectIds: [-1], filterChapterIds: [-1], subjectWeightages: [] })
    } else if (newChapterIds.length === allChapterIds.length && newSubjectIds.length === allSubjectIds.length) {
      updateState({ filterSubjectIds: [], filterChapterIds: [], subjectWeightages: [] })
    } else {
      updateState({ filterSubjectIds: newSubjectIds, filterChapterIds: newChapterIds, subjectWeightages: [] })
    }
  }

  // Handle Individual Chapter Checkbox toggle
  const handleToggleChapter = (chapterId: number, subjectId: number) => {
    const newChaptersSet = new Set(selectedChapterIdsSet)
    if (newChaptersSet.has(chapterId)) {
      newChaptersSet.delete(chapterId)
    } else {
      newChaptersSet.add(chapterId)
    }

    const newChapterIds = Array.from(newChaptersSet)
    const newSubjectIds: number[] = []

    taxonomyTree.forEach(sub => {
      if (sub.chapters.length === 0) {
        if (selectedSubjectIdsSet.has(sub.id)) newSubjectIds.push(sub.id)
      } else {
        if (sub.chapters.some(ch => newChaptersSet.has(ch.id))) {
          newSubjectIds.push(sub.id)
        }
      }
    })

    if (newChapterIds.length === 0 && newSubjectIds.length === 0) {
      updateState({ filterSubjectIds: [-1], filterChapterIds: [-1], subjectWeightages: [] })
    } else if (newChapterIds.length === allChapterIds.length && newSubjectIds.length === allSubjectIds.length) {
      updateState({ filterSubjectIds: [], filterChapterIds: [], subjectWeightages: [] })
    } else {
      updateState({ filterSubjectIds: newSubjectIds, filterChapterIds: newChapterIds, subjectWeightages: [] })
    }
  }

  const handleSelectAllTaxonomy = () => {
    updateState({ filterSubjectIds: [], filterChapterIds: [], subjectWeightages: [] })
  }

  const handleDeselectAllTaxonomy = () => {
    updateState({ filterSubjectIds: [-1], filterChapterIds: [-1], subjectWeightages: [] })
  }

  const handleExpandAll = () => {
    const allExp: Record<number, boolean> = {}
    taxonomyTree.forEach(s => { allExp[s.id] = true })
    setExpandedSubjects(allExp)
  }

  const handleCollapseAll = () => {
    setExpandedSubjects({})
  }

  const toggleSubjectExpand = (subId: number) => {
    setExpandedSubjects(prev => ({ ...prev, [subId]: !prev[subId] }))
  }

  // Filter taxonomy tree by search keyword
  const filteredTaxonomyTree = useMemo(() => {
    const query = taxonomySearch.trim().toLowerCase()
    if (!query) return taxonomyTree

    return taxonomyTree.map(sub => {
      const subjectMatch = sub.name.toLowerCase().includes(query)
      const matchingChapters = sub.chapters.filter(ch =>
        ch.name.toLowerCase().includes(query) ||
        ch.standards.some(st => st.name.toLowerCase().includes(query))
      )
      if (subjectMatch) {
        return sub
      }
      if (matchingChapters.length > 0) {
        return {
          ...sub,
          chapters: matchingChapters
        }
      }
      return null
    }).filter((sub): sub is SubjectItem => sub !== null)
  }, [taxonomyTree, taxonomySearch])

  // Group taxonomy per selected paper section
  const sectionTaxonomyGroups = useMemo(() => {
    if (selectedSections.length === 0) return []
    const query = taxonomySearch.trim().toLowerCase()

    return selectedSections.map((sec: any) => {
      let matchedSubs: SubjectItem[] = []
      if (sec.subject_ids && sec.subject_ids.length > 0) {
        matchedSubs = taxonomyTree.filter(s => sec.subject_ids.includes(s.id))
      } else if (sec.subject_id) {
        matchedSubs = taxonomyTree.filter(s => s.id === sec.subject_id)
      } else if (sec.subject_hint) {
        const hint = sec.subject_hint.toLowerCase()
        matchedSubs = taxonomyTree.filter(s => s.name.toLowerCase().includes(hint) || hint.includes(s.name.toLowerCase()))
      } else {
        matchedSubs = taxonomyTree
      }

      const filteredSubs = matchedSubs.map(sub => {
        let matchedChaps = sub.chapters
        if (sec.chapter_ids && sec.chapter_ids.length > 0) {
          matchedChaps = sub.chapters.filter(ch => sec.chapter_ids.includes(ch.id))
        }

        if (query) {
          const subjectMatch = sub.name.toLowerCase().includes(query)
          const searchChaps = matchedChaps.filter(ch =>
            ch.name.toLowerCase().includes(query) ||
            ch.standards.some(st => st.name.toLowerCase().includes(query))
          )
          if (!subjectMatch && searchChaps.length === 0) return null
          if (!subjectMatch) matchedChaps = searchChaps
        }

        return {
          ...sub,
          chapters: matchedChaps
        }
      }).filter((s): s is SubjectItem => s !== null)

      let poolCount = 0
      filteredSubs.forEach(sub => {
        if (sub.chapters.length === 0) {
          poolCount += (pool?.by_subject?.[sub.id]?.count ?? sub.count)
        } else {
          sub.chapters.forEach(ch => {
            poolCount += (pool?.by_chapter?.[ch.id]?.count ?? ch.count)
          })
        }
      })

      return {
        section: sec,
        subjects: filteredSubs,
        targetQuestions: sec.total_questions || 0,
        poolCount
      }
    })
  }, [selectedSections, taxonomyTree, taxonomySearch, pool])

  // Helper to render a subject node with its chapters
  const renderSubjectNode = (sub: SubjectItem, keyPrefix = '') => {
    const checkState = getSubjectCheckState(sub)
    const isExpanded = !!expandedSubjects[sub.id]
    const isSubjectSelected = checkState !== 'unchecked'

    const subPoolCount = pool?.by_subject?.[sub.id]?.count
    const subBaselineCount = sub.count
    const subDisplayCount = isSubjectSelected && subPoolCount !== undefined ? subPoolCount : subBaselineCount
    const selectedChaptersInSubCount = sub.chapters.filter(ch => selectedChapterIdsSet.has(ch.id)).length

    return (
      <div
        key={`${keyPrefix}-${sub.id}`}
        className={`rounded-xl border transition-all ${
          isSubjectSelected
            ? 'bg-card border-border/80 shadow-2xs'
            : 'bg-muted/10 border-border/40 opacity-75 hover:opacity-100'
        }`}
      >
        {/* Subject Node Header */}
        <div className="flex items-center justify-between p-2 sm:p-3 select-none gap-2">
          <div className="flex items-center gap-2 min-w-0 flex-1">
            {/* Expand / Collapse Button */}
            {sub.chapters.length > 0 ? (
              <button
                type="button"
                onClick={() => toggleSubjectExpand(sub.id)}
                className="p-1 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-colors shrink-0"
                title={isExpanded ? 'Collapse chapters' : 'Expand chapters'}
              >
                {isExpanded ? (
                  <ChevronDown className="h-4 w-4" />
                ) : (
                  <ChevronRight className="h-4 w-4" />
                )}
              </button>
            ) : (
              <span className="w-6 shrink-0" />
            )}

            {/* Subject Checkbox */}
            <button
              type="button"
              onClick={() => handleToggleSubject(sub.id)}
              className="shrink-0 flex items-center justify-center transition-colors"
              title={
                checkState === 'checked'
                  ? `Deselect ${sub.name}`
                  : checkState === 'indeterminate'
                  ? `Select all chapters in ${sub.name}`
                  : `Select ${sub.name}`
              }
            >
              {checkState === 'checked' ? (
                <div className="h-4 w-4 rounded bg-primary text-primary-foreground flex items-center justify-center shadow-2xs shrink-0">
                  <Check className="h-3 w-3 stroke-[3]" />
                </div>
              ) : checkState === 'indeterminate' ? (
                <div className="h-4 w-4 rounded bg-primary text-primary-foreground flex items-center justify-center shadow-2xs shrink-0">
                  <Minus className="h-3 w-3 stroke-[3]" />
                </div>
              ) : (
                <div className="h-4 w-4 rounded border border-border bg-card hover:border-primary/60 transition-colors shrink-0" />
              )}
            </button>

            {/* Subject Title & Summary */}
            <div
              onClick={() => sub.chapters.length > 0 ? toggleSubjectExpand(sub.id) : handleToggleSubject(sub.id)}
              className="flex items-baseline gap-1.5 min-w-0 flex-1 cursor-pointer flex-wrap"
            >
              <span className={`text-xs sm:text-sm font-semibold break-words ${
                isSubjectSelected ? 'text-foreground' : 'text-muted-foreground'
              }`}>
                {sub.name}
              </span>

              {sub.chapters.length > 0 && (
                <span className="text-[10px] text-muted-foreground font-medium whitespace-nowrap">
                  ({checkState === 'checked'
                    ? `${sub.chapters.length} chap`
                    : `${selectedChaptersInSubCount}/${sub.chapters.length} chap`})
                </span>
              )}
            </div>
          </div>

          {/* Question Count Badge */}
          <div className="flex items-center shrink-0">
            <Badge
              variant="outline"
              className={`text-[10px] sm:text-[11px] font-semibold px-1.5 sm:px-2 py-0.5 rounded-md transition-colors whitespace-nowrap ${
                isSubjectSelected
                  ? 'bg-primary/10 text-primary border-primary/20'
                  : 'bg-muted/40 text-muted-foreground border-transparent'
              }`}
            >
              <span className="sm:hidden">{subDisplayCount} Qs</span>
              <span className="hidden sm:inline">{subDisplayCount} Ready Qs</span>
            </Badge>
          </div>
        </div>

        {/* Nested Chapters List */}
        {isExpanded && sub.chapters.length > 0 && (
          <div className="px-1.5 sm:px-3 pb-2.5 sm:pb-3 pt-0.5">
            <div className="pl-2 sm:pl-4 border-l border-border/60 ml-1.5 sm:ml-3 space-y-1.5 py-1">
              {sub.chapters.map(chap => {
                const isChapSelected = selectedChapterIdsSet.has(chap.id)
                const chapPoolCount = pool?.by_chapter?.[chap.id]?.count
                const chapBaselineCount = chap.count
                const chapDisplayCount = isChapSelected && chapPoolCount !== undefined
                  ? chapPoolCount
                  : chapBaselineCount

                return (
                  <div
                    key={chap.id}
                    onClick={() => handleToggleChapter(chap.id, sub.id)}
                    className={`flex items-start justify-between p-1.5 sm:p-2 rounded-lg text-xs cursor-pointer transition-all border gap-2 ${
                      isChapSelected
                        ? 'bg-card border-border/70 hover:bg-muted/30 shadow-2xs'
                        : 'bg-transparent border-transparent text-muted-foreground hover:bg-muted/20 hover:text-foreground'
                    }`}
                  >
                    <div className="flex items-start gap-2 min-w-0 flex-1">
                      {/* Chapter Checkbox */}
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation()
                          handleToggleChapter(chap.id, sub.id)
                        }}
                        className="shrink-0 flex items-center justify-center mt-0.5"
                      >
                        {isChapSelected ? (
                          <div className="h-4 w-4 rounded bg-primary text-primary-foreground flex items-center justify-center shadow-2xs shrink-0">
                            <Check className="h-2.5 w-2.5 stroke-[3]" />
                          </div>
                        ) : (
                          <div className="h-4 w-4 rounded border border-border bg-card hover:border-primary/60 transition-colors shrink-0" />
                        )}
                      </button>

                      {/* Chapter Info */}
                      <div className="min-w-0 flex-1 space-y-1">
                        <div className={`font-medium text-xs leading-snug break-words ${
                          isChapSelected ? 'text-foreground' : 'text-muted-foreground'
                        }`}>
                          {chap.name}
                        </div>

                        {chap.standards && chap.standards.length > 0 && (
                          <div className="flex items-center gap-1 flex-wrap">
                            {chap.standards.map(st => (
                              <Badge
                                key={st.id}
                                variant="secondary"
                                className="text-[9px] px-1.5 py-0 h-4 font-normal bg-muted/60 text-muted-foreground shrink-0 leading-none"
                              >
                                {st.name}
                              </Badge>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Chapter Count Badge */}
                    <div className="flex items-center shrink-0 self-start mt-0.5">
                      <span className={`text-[10px] px-1.5 py-0.5 rounded font-mono font-medium ${
                        isChapSelected
                          ? 'bg-muted text-foreground'
                          : 'text-muted-foreground/60'
                      }`}>
                        {chapDisplayCount} Qs
                      </span>
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        )}
      </div>
    )
  }

  // Function to load filtered pool counts from backend
  const fetchPoolBreakdown = useCallback(async () => {
    setLoadingPool(true)
    try {
      const token = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''
      const res = await fetch('/admin/test_generator/load_pool', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': token,
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          language: state.language,
          syllabus_ids: state.selectedSyllabiIds,
          subject_ids: state.filterSubjectIds,
          chapter_ids: state.filterChapterIds,
          section_ids: state.filterSectionIds,
          standard_ids: state.filterStandardIds,
          difficulties: state.filterDifficulties,
          question_types: state.filterQuestionTypes,
          include_tags: state.includeTags,
          exclude_tags: state.excludeTags,
          flags: {
            pyq_only: state.pyqOnly,
            exemplar_only: state.exemplarOnly
          }
        })
      })

      if (res.ok) {
        const breakdown: PoolBreakdown = await res.json()
        updateState({ poolBreakdown: breakdown })
      }
    } catch (err) {
      console.error('Error fetching pool breakdown:', err)
    } finally {
      setLoadingPool(false)
    }
  }, [
    state.language,
    state.selectedSyllabiIds,
    state.filterSubjectIds,
    state.filterChapterIds,
    state.filterSectionIds,
    state.filterStandardIds,
    state.filterDifficulties,
    state.filterQuestionTypes,
    state.includeTags,
    state.excludeTags,
    state.pyqOnly,
    state.exemplarOnly
  ])

  useEffect(() => {
    fetchPoolBreakdown()
  }, [fetchPoolBreakdown])

  const toggleArrayItem = <T,>(list: T[], item: T): T[] => {
    return list.includes(item) ? list.filter(i => i !== item) : [...list, item]
  }

  const handleClearFilters = () => {
    updateState({
      filterSubjectIds: [],
      filterChapterIds: [],
      filterSectionIds: [],
      filterStandardIds: [],
      filterDifficulties: [],
      filterQuestionTypes: [],
      includeTags: [],
      excludeTags: [],
      pyqOnly: false,
      exemplarOnly: false
    })
  }

  const isTaxonomyFiltered = !isAllTaxonomyIncluded
  const activeFiltersCount =
    (isTaxonomyFiltered ? 1 : 0) +
    state.filterStandardIds.length +
    state.filterDifficulties.length +
    state.filterQuestionTypes.length +
    (state.pyqOnly ? 1 : 0) +
    (state.exemplarOnly ? 1 : 0)

  const selectedSubjectsCount = selectedSubjectIdsSet.size
  const selectedChaptersCount = selectedChapterIdsSet.size

  return (
    <div className="space-y-6">
      {/* Section Header Toolbar */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b pb-4">
        <div>
          <h2 className="text-lg font-bold flex items-center gap-2">
            <Filter className="h-5 w-5 text-primary" />
            Pool Filtering & Refinement
          </h2>
          <p className="text-xs text-muted-foreground mt-0.5">
            Filter ready question bank candidates by taxonomy, difficulty, and question format
          </p>
        </div>
        {activeFiltersCount > 0 && (
          <Button
            variant="ghost"
            size="sm"
            onClick={handleClearFilters}
            className="text-xs text-muted-foreground hover:text-destructive gap-1.5 self-start sm:self-auto"
          >
            <RefreshCw className="h-3.5 w-3.5" /> Clear All Filters ({activeFiltersCount})
          </Button>
        )}
      </div>

      {/* Main Content Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column: Filter Controls */}
        <div className="lg:col-span-2 space-y-6">
          {/* Top Control Bar: Language Select & Toggles */}
          <div className="bg-muted/20 border border-border/70 rounded-xl p-3 flex flex-col sm:flex-row sm:items-center justify-between gap-3 sm:gap-4">
            <div className="flex items-center justify-between sm:justify-start gap-2.5 w-full sm:w-auto">
              <div className="flex items-center gap-2 shrink-0">
                <Globe className="h-4 w-4 text-muted-foreground shrink-0" />
                <Label htmlFor="lang-select" className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
                  Language
                </Label>
              </div>
              <Select
                value={state.language}
                onValueChange={val => updateState({ language: val, subjectWeightages: [] })}
              >
                <SelectTrigger id="lang-select" className="h-8 w-32 sm:w-36 text-xs font-medium border-border bg-background shrink-0">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="rounded-xl border-border bg-popover text-foreground shadow-glass-md z-50">
                  <SelectItem value="en">English (en)</SelectItem>
                  <SelectItem value="hi">Hindi (hi)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center gap-4 sm:gap-5 pt-2 sm:pt-0 border-t sm:border-t-0 border-border/40">
              <label
                onClick={() => updateState({ pyqOnly: !state.pyqOnly, subjectWeightages: [] })}
                className="flex items-center gap-2 cursor-pointer select-none text-xs font-medium text-foreground hover:text-primary transition-colors"
              >
                <div
                  className={`h-4 w-4 rounded border flex items-center justify-center shrink-0 transition-colors ${
                    state.pyqOnly
                      ? 'bg-primary border-primary text-primary-foreground shadow-2xs'
                      : 'border-border bg-card hover:border-primary/60'
                  }`}
                >
                  {state.pyqOnly && <Check className="h-3 w-3 stroke-[3]" />}
                </div>
                <span>PYQ Only</span>
              </label>

              <label
                onClick={() => updateState({ exemplarOnly: !state.exemplarOnly, subjectWeightages: [] })}
                className="flex items-center gap-2 cursor-pointer select-none text-xs font-medium text-foreground hover:text-primary transition-colors"
              >
                <div
                  className={`h-4 w-4 rounded border flex items-center justify-center shrink-0 transition-colors ${
                    state.exemplarOnly
                      ? 'bg-primary border-primary text-primary-foreground shadow-2xs'
                      : 'border-border bg-card hover:border-primary/60'
                  }`}
                >
                  {state.exemplarOnly && <Check className="h-3 w-3 stroke-[3]" />}
                </div>
                <span>Exemplar Only</span>
              </label>
            </div>
          </div>

          {/* ─── Nested Taxonomy Tree: Subjects & Chapters ────────────────────────────── */}
          {taxonomyTree.length > 0 && (
            <div className="space-y-3">
              {/* Tree Header & Actions Toolbar */}
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 bg-muted/30 p-3 rounded-xl border">
                <div className="flex items-center gap-2 flex-wrap">
                  <FolderTree className="h-4 w-4 text-primary" />
                  <Label className="text-xs font-bold uppercase tracking-wider text-foreground">
                    Taxonomy Filtering
                  </Label>
                  <Badge variant="outline" className="text-[10px] font-semibold bg-background px-2 py-0.5 rounded-md">
                    {isAllTaxonomyIncluded
                      ? 'All Included'
                      : isNoneTaxonomyIncluded
                      ? 'None Selected'
                      : `${selectedSubjectsCount}/${allSubjectIds.length} Subjects • ${selectedChaptersCount}/${allChapterIds.length} Chapters`}
                  </Badge>
                </div>

                <div className="flex flex-wrap items-center gap-2 text-xs">
                  {/* View Mode Switcher when sections are selected */}
                  {selectedSections.length > 0 && (
                    <div className="flex items-center gap-1 border rounded-lg p-0.5 bg-card">
                      <button
                        type="button"
                        onClick={() => setViewMode('by_section')}
                        className={`px-2 py-0.5 text-[11px] font-semibold rounded flex items-center gap-1 transition-colors ${
                          viewMode === 'by_section'
                            ? 'bg-primary text-primary-foreground shadow-2xs'
                            : 'text-muted-foreground hover:text-foreground'
                        }`}
                      >
                        <Layers className="h-3 w-3" /> Per Section ({selectedSections.length})
                      </button>
                      <button
                        type="button"
                        onClick={() => setViewMode('all_subjects')}
                        className={`px-2 py-0.5 text-[11px] font-semibold rounded flex items-center gap-1 transition-colors ${
                          viewMode === 'all_subjects'
                            ? 'bg-primary text-primary-foreground shadow-2xs'
                            : 'text-muted-foreground hover:text-foreground'
                        }`}
                      >
                        <BookOpen className="h-3 w-3" /> All Subjects
                      </button>
                    </div>
                  )}

                  {/* Quick Expand / Collapse Controls */}
                  <div className="flex items-center gap-1 border rounded-lg p-0.5 bg-card">
                    <button
                      type="button"
                      onClick={handleExpandAll}
                      className="px-2 py-0.5 text-[11px] font-medium text-muted-foreground hover:text-foreground rounded hover:bg-muted transition-colors"
                      title="Expand all subjects"
                    >
                      Expand All
                    </button>
                    <span className="text-muted-foreground/30 text-[10px]">|</span>
                    <button
                      type="button"
                      onClick={handleCollapseAll}
                      className="px-2 py-0.5 text-[11px] font-medium text-muted-foreground hover:text-foreground rounded hover:bg-muted transition-colors"
                      title="Collapse all subjects"
                    >
                      Collapse All
                    </button>
                  </div>

                  {/* Select All / Deselect All */}
                  <div className="flex items-center gap-1 border rounded-lg p-0.5 bg-card">
                    <button
                      type="button"
                      onClick={handleSelectAllTaxonomy}
                      disabled={isAllTaxonomyIncluded}
                      className={`px-2 py-0.5 text-[11px] font-semibold rounded transition-colors ${
                        isAllTaxonomyIncluded
                          ? 'text-muted-foreground/50 cursor-not-allowed'
                          : 'text-primary hover:bg-primary/10'
                      }`}
                    >
                      Select All
                    </button>
                    <span className="text-muted-foreground/30 text-[10px]">|</span>
                    <button
                      type="button"
                      onClick={handleDeselectAllTaxonomy}
                      disabled={isNoneTaxonomyIncluded}
                      className={`px-2 py-0.5 text-[11px] font-medium rounded transition-colors ${
                        isNoneTaxonomyIncluded
                          ? 'text-muted-foreground/50 cursor-not-allowed'
                          : 'text-muted-foreground hover:text-destructive hover:bg-destructive/10'
                      }`}
                    >
                      Deselect All
                    </button>
                  </div>
                </div>
              </div>

              {/* Taxonomy Search Input if > 2 subjects or chapters */}
              {(allSubjectIds.length > 2 || allChapterIds.length > 5) && (
                <div className="relative">
                  <Search className="h-3.5 w-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    type="text"
                    value={taxonomySearch}
                    onChange={e => setTaxonomySearch(e.target.value)}
                    placeholder="Search subjects or chapters..."
                    className="h-8 pl-8 pr-3 text-xs bg-background rounded-lg border focus:ring-1 focus:ring-primary"
                  />
                  {taxonomySearch && (
                    <button
                      onClick={() => setTaxonomySearch('')}
                      className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground text-xs"
                    >
                      ✕
                    </button>
                  )}
                </div>
              )}

              {/* Tree Container */}
              <div className="space-y-4">
                {selectedSections.length > 0 && viewMode === 'by_section' ? (
                  /* Section-wise Hierarchical View */
                  sectionTaxonomyGroups.length === 0 ? (
                    <div className="p-4 text-center text-xs text-muted-foreground bg-muted/20 rounded-xl border">
                      No matching sections or chapters found.
                    </div>
                  ) : (
                    sectionTaxonomyGroups.map((group, idx) => {
                      const isSufficient = group.poolCount >= group.targetQuestions
                      return (
                        <div
                          key={group.section.id || idx}
                          className="rounded-2xl border border-border/80 bg-muted/15 p-2.5 sm:p-3.5 space-y-3 shadow-glass-2xs"
                        >
                          <div className="space-y-2 border-b border-border/60 pb-2.5">
                            <div className="flex items-center gap-2 min-w-0">
                              <Badge variant="outline" className="text-[10px] font-bold bg-primary/10 text-primary border-primary/25 shrink-0">
                                Section {idx + 1}
                              </Badge>
                              <span className="text-xs sm:text-sm font-bold text-foreground break-words flex-1">
                                {group.section.name}
                              </span>
                            </div>

                            <div className="flex items-center gap-1.5 flex-wrap text-xs">
                              <span className="text-[10px] text-muted-foreground bg-card px-1.5 py-0.5 rounded-md border border-border/70">
                                {group.targetQuestions} Target Qs
                              </span>
                              {group.section.duration_minutes && (
                                <span className="text-[10px] text-muted-foreground bg-card px-1.5 py-0.5 rounded-md border border-border/70">
                                  {group.section.duration_minutes}m timer
                                </span>
                              )}
                              <Badge
                                variant="outline"
                                className={`text-[10px] font-semibold px-2 py-0.5 rounded-md ${
                                  isSufficient
                                    ? 'bg-card text-primary border-primary/30'
                                    : 'bg-destructive/10 text-destructive border-destructive/30'
                                }`}
                              >
                                {group.poolCount} / {group.targetQuestions} Ready
                              </Badge>
                            </div>
                          </div>

                          {group.subjects.length === 0 ? (
                            <div className="text-xs text-muted-foreground p-3 text-center bg-card rounded-xl border">
                              No matching subjects found for this section pattern.
                            </div>
                          ) : (
                            <div className="space-y-2">
                              {group.subjects.map(sub => renderSubjectNode(sub, `sec-${group.section.id || idx}`))}
                            </div>
                          )}
                        </div>
                      )
                    })
                  )
                ) : (
                  /* Unified All Subjects View */
                  filteredTaxonomyTree.length === 0 ? (
                    <div className="p-4 text-center text-xs text-muted-foreground bg-muted/20 rounded-xl border">
                      No subjects or chapters match &quot;{taxonomySearch}&quot;
                    </div>
                  ) : (
                    filteredTaxonomyTree.map(sub => renderSubjectNode(sub, 'all'))
                  )
                )}
              </div>
            </div>
          )}

          {/* Standards Filter */}
          {standards.length > 0 && (
            <div className="space-y-2 pt-2 border-t">
              <Label className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Standards / Class</Label>
              <div className="flex flex-wrap gap-1.5">
                {standards.map(st => {
                  const isSelected = state.filterStandardIds.includes(st.id)
                  const count = pool?.by_standard?.[st.id] ?? pool?.by_standard?.[String(st.id)] ?? 0
                  return (
                    <button
                      key={st.id}
                      onClick={() => updateState({ filterStandardIds: toggleArrayItem(state.filterStandardIds, st.id), subjectWeightages: [] })}
                      className={`px-2.5 py-1 rounded-md text-xs transition-colors flex items-center gap-1.5 ${
                        isSelected
                          ? 'bg-primary text-primary-foreground font-medium shadow-2xs'
                          : 'bg-muted/30 hover:bg-muted/60 text-muted-foreground hover:text-foreground border border-transparent'
                      }`}
                    >
                      <span>{st.name}</span>
                      <span
                        className={`text-[10px] px-1.5 py-0.2 rounded font-semibold ${
                          isSelected
                            ? 'bg-primary-foreground/20 text-primary-foreground'
                            : 'text-muted-foreground/70 font-medium'
                        }`}
                      >
                        {count}
                      </span>
                    </button>
                  )
                })}
              </div>
            </div>
          )}

          {/* Difficulty & Question Type */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-2 border-t">
            <div className="space-y-2">
              <Label className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Difficulty Levels</Label>
              <div className="flex flex-wrap gap-1.5">
                {DIFFICULTY_OPTIONS.map(diff => {
                  const isSelected = state.filterDifficulties.includes(diff)
                  const count = pool?.by_difficulty?.[diff] || 0
                  return (
                    <button
                      key={diff}
                      onClick={() => updateState({ filterDifficulties: toggleArrayItem(state.filterDifficulties, diff), subjectWeightages: [] })}
                      className={`px-2.5 py-1 rounded-md text-xs transition-colors flex items-center gap-1.5 ${
                        isSelected
                          ? 'bg-primary text-primary-foreground font-medium shadow-2xs'
                          : 'bg-muted/30 hover:bg-muted/60 text-muted-foreground hover:text-foreground border border-transparent'
                      }`}
                    >
                      <span>{diff}</span>
                      <span
                        className={`text-[10px] px-1.5 py-0.2 rounded font-semibold ${
                          isSelected
                            ? 'bg-primary-foreground/20 text-primary-foreground'
                            : 'text-muted-foreground/70 font-medium'
                        }`}
                      >
                        {count}
                      </span>
                    </button>
                  )
                })}
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Question Formats</Label>
              <div className="flex flex-wrap gap-1.5">
                {QUESTION_TYPE_OPTIONS.map(qtype => {
                  const isSelected = state.filterQuestionTypes.includes(qtype) ||
                                     state.filterQuestionTypes.includes(qtype.toUpperCase()) ||
                                     state.filterQuestionTypes.includes(qtype.toLowerCase())
                  const count =
                    pool?.by_type?.[qtype] ??
                    pool?.by_type?.[qtype.toUpperCase()] ??
                    pool?.by_type?.[qtype.toLowerCase()] ??
                    0
                  return (
                    <button
                      key={qtype}
                      onClick={() => updateState({ filterQuestionTypes: toggleArrayItem(state.filterQuestionTypes, qtype), subjectWeightages: [] })}
                      className={`px-2.5 py-1 rounded-md text-xs uppercase transition-colors flex items-center gap-1.5 ${
                        isSelected
                          ? 'bg-primary text-primary-foreground font-medium shadow-2xs'
                          : 'bg-muted/30 hover:bg-muted/60 text-muted-foreground hover:text-foreground border border-transparent'
                      }`}
                    >
                      <span>{qtype.replace('_', ' ')}</span>
                      <span
                        className={`text-[10px] px-1.5 py-0.2 rounded font-semibold lowercase ${
                          isSelected
                            ? 'bg-primary-foreground/20 text-primary-foreground'
                            : 'text-muted-foreground/70 font-medium'
                        }`}
                      >
                        {count}
                      </span>
                    </button>
                  )
                })}
              </div>
            </div>
          </div>
        </div>

        {/* Right Column: Live Pool Breakdown Sidebar */}
        <div>
          <div className="bg-card border rounded-xl p-5 shadow-sm space-y-5 sticky top-4">
            <div className="flex items-center justify-between border-b pb-3">
              <span className="text-sm font-bold flex items-center gap-2">
                <BarChart2 className="h-4 w-4 text-primary" /> Live Pool Breakdown
              </span>
              {loadingPool && <Loader2 className="h-4 w-4 animate-spin text-primary" />}
            </div>

            {/* Total Stat Badge */}
            <div className="bg-primary/10 border border-primary/20 rounded-xl p-4 text-center space-y-1">
              <span className="text-[11px] font-bold uppercase tracking-wider text-primary">
                Available Matching Questions
              </span>
              <div className="text-4xl font-black text-primary">
                {pool?.total !== undefined ? pool.total.toLocaleString() : '...'}
              </div>
              <p className="text-[11px] text-muted-foreground">
                Qualified for test generator selection
              </p>
            </div>

            {/* Zero Pool Warning */}
            {pool !== null && pool.total === 0 && !loadingPool && (
              <div className="p-3 bg-destructive/10 border border-destructive/20 rounded-lg text-xs text-destructive space-y-1">
                <p className="font-bold">No Matching Questions</p>
                <p className="text-[11px] text-muted-foreground">
                  Adjust your subject, chapter, or difficulty filters to include questions in the pool.
                </p>
              </div>
            )}

            {/* Subject Distribution */}
            {pool?.by_subject && Object.keys(pool.by_subject).length > 0 && (
              <div className="space-y-3">
                <span className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
                  Subject Share
                </span>
                <div className="space-y-2">
                  {Object.entries(pool.by_subject).map(([sid, sb]) => {
                    const percent = pool.total > 0 ? Math.round((sb.count / pool.total) * 100) : 0
                    return (
                      <div key={sid} className="text-xs space-y-1">
                        <div className="flex justify-between font-medium">
                          <span>{sb.name}</span>
                          <span className="text-muted-foreground font-semibold">{sb.count} ({percent}%)</span>
                        </div>
                        <div className="h-1.5 w-full bg-muted rounded-full overflow-hidden">
                          <div
                            className="h-full bg-primary transition-all duration-300"
                            style={{ width: `${percent}%` }}
                          />
                        </div>
                      </div>
                    )
                  })}
                </div>
              </div>
            )}

            {/* Difficulty Breakdown */}
            {pool?.by_difficulty && (
              <div className="space-y-2 pt-3 border-t">
                <span className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
                  Difficulty Breakdown
                </span>
                <div className="grid grid-cols-3 gap-2 text-center text-xs">
                  {['Easy', 'Medium', 'Hard'].map(d => (
                    <div key={d} className="p-2 rounded-lg bg-muted/30 border">
                      <div className="font-bold text-foreground">{pool.by_difficulty[d] || 0}</div>
                      <div className="text-[10px] text-muted-foreground font-medium">{d}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Standards Breakdown */}
            {pool?.by_standard && Object.keys(pool.by_standard).length > 0 && (
              <div className="space-y-2 pt-3 border-t">
                <span className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
                  Standards / Class
                </span>
                <div className="flex flex-wrap gap-1.5">
                  {standards.map(st => {
                    const stCount = pool.by_standard?.[st.id] ?? pool.by_standard?.[String(st.id)] ?? 0
                    if (stCount === 0) return null
                    return (
                      <Badge key={st.id} variant="outline" className="text-[10px] font-semibold bg-primary/10 text-primary border-primary/20">
                        {st.name}: {stCount}
                      </Badge>
                    )
                  })}
                </div>
              </div>
            )}

            {/* Question Types Breakdown */}
            {pool?.by_type && Object.keys(pool.by_type).length > 0 && (
              <div className="space-y-2 pt-3 border-t">
                <span className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
                  Question Formats
                </span>
                <div className="flex flex-wrap gap-1.5">
                  {Object.entries(pool.by_type).map(([t, count]) => (
                    <Badge key={t} variant="outline" className="text-[10px] uppercase font-semibold bg-primary/10 text-primary border-primary/20">
                      {t.replace('_', ' ')}: {count}
                    </Badge>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
